import BootcampCompletion from "@Components/game/session-results/bootcamp/bootcamp-completion";
import BootcampFinalCompletion from "@Components/game/session-results/bootcamp/bootcamp-final-completion";
import FeedbackPrefetch from "@Components/game/session-results/feedback/feedback-prefetch";
import FeedbackSection from "@Components/game/session-results/feedback/feedback-section";
import SessionResultsHeader from "@Components/game/session-results/header/header";
import OverviewSection from "@Components/game/session-results/overview/overview-section";
import SessionResultsBar from "@Components/game/session-results/results-bar/results-bar";
import TrainingStatsSection from "@Components/game/session-results/training-stats/training-stats-section";
import ContainerWrapper from "@Components/generic/container-wrapper";
import ScrollfulContainer from "@Components/generic/scrollful-container";
import { BootcampChallengeStep } from "@Constants/bootcamp";
import { HeaderBackground } from "@Containers/game/results/styled-components";
import { Events } from "@Context/common/general/context";
import {
  SessionResultsPageProvider,
  useSessionResultsStaticContext,
  useSessionResultsTabContext,
} from "@Context/game/activity/session-results-context";
import { GameModes } from "@Enums/index";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { useTrainingPlansProgressToastTrigger } from "@hooks/game/training-plans/use-training-plans-progress-toast-trigger";

import { Box, Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

const pageId = "SESSION_RESULTS";

const SessionResultsContent = () => {
  const theme = useTheme();
  const { currentTab } = useSessionResultsTabContext();
  const { sessionData, sessionResults } = useSessionResultsStaticContext();
  const bootcampStep = sessionData?.bootcampStep;
  useTrainingPlansProgressToastTrigger();
  const isMatchPlay = sessionResults?.game_mode === GameModes.MATCH_PLAY;
  const isMatchplayAnalysisTab = isMatchPlay && currentTab === 1;
  const [hasMountedMatchplayAnalysis, setHasMountedMatchplayAnalysis] = useState(false);

  useEffect(() => {
    Events.loading.resetLoader.emit();
  }, [currentTab]);

  useEffect(() => {
    if (!isMatchplayAnalysisTab || hasMountedMatchplayAnalysis) {
      return;
    }

    const frameId = requestAnimationFrame(() => setHasMountedMatchplayAnalysis(true));
    return () => cancelAnimationFrame(frameId);
  }, [hasMountedMatchplayAnalysis, isMatchplayAnalysisTab]);

  if (bootcampStep === BootcampChallengeStep.FINAL && sessionResults?.failed !== true) {
    return (
      <ContainerWrapper id={pageId} sx={{ overflow: "visible" }} fullHeight>
        <ScrollfulContainer maxWidth={false} disableGutters>
          <HeaderBackground />
          <Stack position="relative" zIndex={1} marginTop={10} paddingTop={theme.spacing(5)} paddingBottom={theme.spacing(3)} gap={4}>
            <SessionResultsBar />
            <FeedbackPrefetch />
            <FeedbackSection showHeader={false} />
            <BootcampFinalCompletion />
          </Stack>
        </ScrollfulContainer>
      </ContainerWrapper>
    );
  }

  if (bootcampStep && bootcampStep !== BootcampChallengeStep.FINAL) {
    return (
      <ContainerWrapper id={pageId} sx={{ overflow: "visible" }} fullHeight>
        <ScrollfulContainer maxWidth={false} disableGutters>
          <HeaderBackground sx={{ position: "absolute", inset: 0, height: "100%" }} />
          <BootcampCompletion challengeStep={bootcampStep} />
        </ScrollfulContainer>
      </ContainerWrapper>
    );
  }

  return (
    <ContainerWrapper id={pageId} sx={{ overflow: "visible" }} fullHeight>
      <ScrollfulContainer maxWidth={false} disableGutters>
        <HeaderBackground />
        <Stack position="relative" zIndex={1} paddingTop={10} paddingBottom={currentTab === 0 ? theme.spacing(3) : theme.spacing(1)}>
          <SessionResultsBar />
          <SessionResultsHeader />
          <Stack position="relative" gap={4}>
            <FeedbackPrefetch />

            {isMatchPlay ? (
              <>
                {!isMatchplayAnalysisTab && (
                  <Stack gap={4}>
                    <OverviewSection />
                    <TrainingStatsSection />
                  </Stack>
                )}

                <Box display={isMatchplayAnalysisTab ? "block" : "none"}>{hasMountedMatchplayAnalysis && <FeedbackSection showHeader={false} />}</Box>
              </>
            ) : currentTab === 0 ? (
              <>
                <OverviewSection />
                <TrainingStatsSection />
              </>
            ) : (
              <>
                <FeedbackSection />
                <OverviewSection />
              </>
            )}
          </Stack>
        </Stack>
      </ScrollfulContainer>
    </ContainerWrapper>
  );
};

const SessionResults = ({ headerButtonType }: { headerButtonType: VRHeaderButtonType }) => {
  return (
    <SessionResultsPageProvider headerButtonType={headerButtonType}>
      <SessionResultsContent />
    </SessionResultsPageProvider>
  );
};

export default SessionResults;
